import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { engagementStandardTierKeys } from "@/lib/query";

/**
 * Story E4 (docs/specs/discipline-rating-fit-score.md §4): the per-tier
 * Expectation Match penalty step `P` — `memberScore = max(0, 100 − P × |deviation|)`.
 * Configuration value, never hard-coded in the scoring engine. Default 25.
 */
export interface TierExpectationPenaltyStep {
  id: string;
  name: string;
  expectationPenaltyStep: number;
}

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

export function useTierExpectationPenaltyStep(tierId: string, options?: { enabled?: boolean }) {
  const enabled = (options?.enabled ?? true) && tierId.length > 0;
  return useQuery<TierExpectationPenaltyStep>({
    queryKey: engagementStandardTierKeys.expectationPenaltyStep(tierId),
    queryFn: () =>
      jsonFetch<TierExpectationPenaltyStep>(
        `/api/engagement-standard-tiers/${tierId}/expectation-penalty-step`,
      ),
    enabled,
  });
}

/** `[RM]` (and CEO, via manageConfiguration) edits `P` per tier. Audited. */
export function useSetTierExpectationPenaltyStep(tierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (expectationPenaltyStep: number) =>
      jsonFetch<TierExpectationPenaltyStep>(
        `/api/engagement-standard-tiers/${tierId}/expectation-penalty-step`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ expectationPenaltyStep }),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: engagementStandardTierKeys.expectationPenaltyStep(tierId) });
      toast.success("Expectation Match penalty step updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
